import React, { useEffect, useRef, useState } from 'react';
import { TextArea } from '../../../components/form';
import VirtualKeyboard from '../../../common/VirtualKeyboard';

export default function SectionDetailsEditor({
  sectionKey,
  sectionName,
  sectionDescription,
  isVirtualKeyboardEnabled,
  onSectionFieldChange,
  onSectionEdit,
  namePlaceholder = 'Add a section name',
  descriptionPlaceholder = 'Add a section description',
  nameWidth = '250px'
}) {
  const [nameDraft, setNameDraft] = useState(sectionName || '');
  const [descriptionDraft, setDescriptionDraft] = useState(sectionDescription || '');
  const [activeField, setActiveField] = useState(null);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const keyboardContainerRef = useRef(null);
  const nameInputRef = useRef(null);
  const descriptionInputRef = useRef(null);

  useEffect(() => {
    setNameDraft(sectionName || '');
    setDescriptionDraft(sectionDescription || '');
    setActiveField(null);
    setKeyboardVisible(false);
  }, [sectionKey]);

  useEffect(() => {
    const handleClickOutside = event => {
      const ignore = event?.target?.closest?.(
        '[data-virtualkeyboard-ignore="true"], .hg-theme-default, .simple-keyboard, .hg-button, .hg-rows'
      );
      if (ignore) return;
      if (keyboardVisible && keyboardContainerRef.current && !keyboardContainerRef.current.contains(event.target)) {
        setKeyboardVisible(false);
      }
    };

    if (keyboardVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [keyboardVisible]);

  const normalizeDescription = value => {
    return value && value !== 'null' ? value : '';
  };

  const handleFieldFocus = fieldName => {
    if (!isVirtualKeyboardEnabled) return;
    setActiveField(fieldName);
    setKeyboardVisible(true);
  };

  const setCaretToEnd = ref => {
    if (!ref?.current) return;
    const value = ref.current.value || '';
    const pos = value.length;
    ref.current.focus();
    ref.current.setSelectionRange(pos, pos);
  };

  const handleNameChange = value => {
    setNameDraft(value);
    onSectionFieldChange('sectionName', value);
    onSectionEdit();
  };

  const handleDescriptionChange = value => {
    setDescriptionDraft(value);
    onSectionFieldChange('sectionDescription', value);
    onSectionEdit();
  };

  const handleKeyboardChange = input => {
    if (activeField === 'sectionName') {
      handleNameChange(input);
      if (nameInputRef.current) {
        nameInputRef.current.value = input;
        setCaretToEnd(nameInputRef);
      }
      return;
    }
    if (activeField === 'sectionDescription') {
      handleDescriptionChange(input);
      if (descriptionInputRef.current) {
        descriptionInputRef.current.value = input;
        setCaretToEnd(descriptionInputRef);
      }
    }
  };

  return (
    <>
      <TextArea
        showCharacters={false}
        fontSize="1.125rem"
        value={nameDraft}
        useDefaultValue={true}
        placeholder={namePlaceholder}
        width={nameWidth}
        style={{ color: 'var(--deepBlue)' }}
        onChange={e => handleNameChange(e.target.value)}
        onFocus={() => handleFieldFocus('sectionName')}
        onClick={() => handleFieldFocus('sectionName')}
        data-virtualkeyboard-ignore="true"
        inputRef={nameInputRef}
      />
      <TextArea
        fontSize="0.75rem"
        showCharacters={false}
        value={normalizeDescription(descriptionDraft)}
        placeholder={descriptionPlaceholder}
        className="mt-1"
        style={{ color: 'var(--deepBlue)', fontWeight: '500' }}
        onChange={e => handleDescriptionChange(e.target.value)}
        onFocus={() => handleFieldFocus('sectionDescription')}
        onClick={() => handleFieldFocus('sectionDescription')}
        data-virtualkeyboard-ignore="true"
        inputRef={descriptionInputRef}
      />
      {isVirtualKeyboardEnabled && keyboardVisible && (activeField === 'sectionName' || activeField === 'sectionDescription') && (
        <div ref={keyboardContainerRef}>
          <VirtualKeyboard
            isVisible={keyboardVisible}
            fieldKey={activeField}
            value={activeField === 'sectionName' ? nameDraft : normalizeDescription(descriptionDraft)}
            onChange={handleKeyboardChange}
            onClose={() => setKeyboardVisible(false)}
          />
        </div>
      )}
    </>
  );
}
